import { useState, useSyncExternalStore } from 'react';

import { ROUTE42_ASSISTANT_NAME } from '../../../config/identity';

export function ConversationComposer({
  machineName,
  mobileMachineName,
  disabled,
  busy,
  initialValue = '',
  onSubmit,
}: {
  machineName: string;
  mobileMachineName?: string;
  disabled?: boolean;
  busy?: boolean;
  initialValue?: string;
  onSubmit: (question: string) => Promise<void> | void;
}) {
  const [value, setValue] = useState(initialValue);
  const isMobile = useSyncExternalStore(subscribeMobileComposer, isMobileComposer, () => false);
  const hasText = Boolean(value.trim());
  const sendActive = !disabled && (hasText || Boolean(busy));
  const placeholderName = isMobile && mobileMachineName ? mobileMachineName : machineName;

  async function submit() {
    const question = value.trim();
    if (!question || disabled || busy) return;
    setValue('');
    try {
      await onSubmit(question);
    } catch {
      setValue(question);
    }
  }

  return (
    <div className={`r42-composer${disabled ? ' r42-composer--disabled' : ''}`}>
      <textarea
        aria-label={`Ask ${ROUTE42_ASSISTANT_NAME}`}
        value={value}
        disabled={disabled || busy}
        placeholder={`Ask ${ROUTE42_ASSISTANT_NAME} about ${placeholderName}...`}
        rows={1}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void submit();
          }
        }}
      />
      <button
        className={`r42-composer-send${sendActive ? ' is-active' : ''}${busy ? ' is-busy' : ''}`}
        disabled={disabled || busy || !hasText}
        aria-label="Send question"
        aria-busy={busy || undefined}
        onClick={() => void submit()}
      >
        {busy ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-arrow-up" aria-hidden="true" />}
      </button>
    </div>
  );
}

function isMobileComposer(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 575px)').matches;
}

function subscribeMobileComposer(callback: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
  const media = window.matchMedia('(max-width: 575px)');
  media.addEventListener?.('change', callback);
  return () => media.removeEventListener?.('change', callback);
}
